import { ReactNode, useEffect, useRef, useState } from 'react'
import { Editor as TiptapEditor } from '@tiptap/react'

import { cn } from '@/utils'
import {
  CheckboxIcon,
  ListBulletIcon,
  FontBoldIcon,
  FontItalicIcon,
  UnderlineIcon,
  TextAlignLeftIcon,
  TextAlignCenterIcon,
  TextAlignRightIcon,
  OpacityIcon,
  CheckIcon,
} from '@radix-ui/react-icons'
import { WrenchIcon } from '@heroicons/react/24/outline'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { OrderedListIcon } from '@/components/icons/OrderedListIcon'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ExtensionsEnum, HIGHLIGHT_COLORS, TEXT_COLORS } from './constants'
import { IconsMenu } from './IconsMenu'

const Button = ({
  children,
  onClick,
  isActive,
}: {
  children: ReactNode
  onClick?: () => void
  isActive?: boolean
}) => {
  return (
    <button
      type="button"
      className={cn(
        'h-8 w-8 p-0 rounded-md hover:bg-accent flex items-center justify-center',
        {
          'bg-primary-45 text-white hover:bg-primary-45': isActive,
        },
      )}
      onClick={() => {
        if (onClick) onClick()
      }}
    >
      {children}
    </button>
  )
}

const Trigger = ({
  children,
  isActive,
}: {
  children: ReactNode
  isActive?: boolean
}) => {
  return (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className={cn(
          'h-8 w-8 p-0 rounded-md hover:bg-accent flex items-center justify-center',
          {
            'bg-primary-45 text-white hover:bg-primary-45': isActive,
          },
        )}
      >
        {children}
      </button>
    </DropdownMenuTrigger>
  )
}

const ColorPicker = ({
  whiteCheck,
  colors,
  onColorSelect,
  isSelected,
}: {
  whiteCheck?: boolean
  colors: Array<{ value: string; name: string }>
  onColorSelect: (color: string) => void
  isSelected?: (color: string) => boolean
}) => (
  <>
    {colors.map((color) => (
      <DropdownMenuItem
        key={color.value}
        className={cn('justify-start p-1')}
        onClick={() => onColorSelect(color.value)}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer"
                style={{
                  backgroundColor: color.value,
                  border: '2px solid #d1d5db',
                }}
              >
                {isSelected?.(color.value) && (
                  <CheckIcon
                    className={cn('w-3 h-3', { 'text-white': whiteCheck })}
                  />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              {color.name}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DropdownMenuItem>
    ))}
  </>
)

export const EditorMenuBar = ({
  editor,
  extensionsList,
  backgroundColor,
  updateBackgroundColorHandler,
  onClickOutside,
}: {
  editor: TiptapEditor
  backgroundColor?: string
  extensionsList: Array<ExtensionsEnum>
  updateBackgroundColorHandler?: (color: string) => void
  onClickOutside?: () => void
}) => {
  const menuBarRef = useRef<HTMLDivElement>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [, forceUpdate] = useState({})

  // Force re-render when editor state changes
  useEffect(() => {
    const handleUpdate = () => {
      forceUpdate({})
    }

    editor.on('transaction', handleUpdate)
    editor.on('selectionUpdate', handleUpdate)

    return () => {
      editor.off('transaction', handleUpdate)
      editor.off('selectionUpdate', handleUpdate)
    }
  }, [editor])

  useEffect(() => {
    if (!onClickOutside) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target

      if (!(target instanceof Node)) return

      if (menuBarRef.current?.contains(target)) return

      const editorElement = document.querySelector(
        '[data-testid="tip-tap-editor"]',
      )
      if (editorElement?.contains(target)) return

      if (target instanceof Element) {
        const dropdownContent = target.closest(
          '[role="menu"], [data-radix-dropdown-menu-content]',
        )
        if (dropdownContent) return
      }

      onClickOutside()
    }

    document.addEventListener('mousedown', handleClickOutside, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [onClickOutside])

  return (
    <div
      ref={menuBarRef}
      className="bg-white border border-gray-200 rounded-md shadow-md p-0.5 gap-1 h-auto flex items-center space-x-1"
      onMouseDown={(e) => {
        e.preventDefault()
      }}
    >
      <Button
        isActive={editor.isActive('bold')}
        onClick={() => {
          setOpenDropdown(null)
          editor.chain().focus().toggleBold().run()
        }}
      >
        <FontBoldIcon className="w-5 h-5" />
      </Button>
      <Button
        isActive={editor.isActive('italic')}
        onClick={() => {
          setOpenDropdown(null)
          editor.chain().focus().toggleItalic().run()
        }}
      >
        <FontItalicIcon className="w-5 h-5" />
      </Button>
      {extensionsList.includes(ExtensionsEnum.UNDERLINE) && (
        <Button
          isActive={editor.isActive('underline')}
          onClick={() => {
            setOpenDropdown(null)
            editor.chain().focus().toggleUnderline().run()
          }}
        >
          <UnderlineIcon className="w-5 h-5" />
        </Button>
      )}
      {extensionsList.includes(ExtensionsEnum.TASK_LIST) && (
        <DropdownMenu
          open={openDropdown === 'lists'}
          onOpenChange={(open) => setOpenDropdown(open ? 'lists' : null)}
        >
          <Trigger>
            <ListBulletIcon className="w-5 h-5" />
          </Trigger>
          <DropdownMenuContent
            className="min-w-[2rem]"
            onMouseDown={(e) => e.preventDefault()}
          >
            <DropdownMenuItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive('bulletList'),
              })}
              onClick={() => {
                // Defer focus until after dropdown closes to prevent blur
                setTimeout(() =>
                  editor.chain().focus().toggleBulletList().run(),
                )
              }}
            >
              <ListBulletIcon className="w-5 h-5" />
            </DropdownMenuItem>
            <DropdownMenuItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive('orderedList'),
              })}
              onClick={() => {
                setTimeout(() =>
                  editor.chain().focus().toggleOrderedList().run(),
                )
              }}
            >
              <OrderedListIcon className="w-5 h-5" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.TEXT_ALIGN) && (
        <DropdownMenu
          open={openDropdown === 'align'}
          onOpenChange={(open) => setOpenDropdown(open ? 'align' : null)}
        >
          <Trigger>
            <TextAlignLeftIcon className="w-5 h-5" />
          </Trigger>
          <DropdownMenuContent
            className="min-w-[2rem]"
            onMouseDown={(e) => e.preventDefault()}
          >
            <DropdownMenuItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive({
                  textAlign: 'left',
                }),
              })}
              onClick={() => {
                setTimeout(() =>
                  editor.chain().focus().setTextAlign('left').run(),
                )
              }}
            >
              <TextAlignLeftIcon className="w-5 h-5" />
            </DropdownMenuItem>
            <DropdownMenuItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive({
                  textAlign: 'center',
                }),
              })}
              onClick={() => {
                setTimeout(() =>
                  editor.chain().focus().setTextAlign('center').run(),
                )
              }}
            >
              <TextAlignCenterIcon className="w-5 h-5" />
            </DropdownMenuItem>
            <DropdownMenuItem
              className={cn({
                'bg-primary-45 text-white': editor.isActive({
                  textAlign: 'right',
                }),
              })}
              onClick={() => {
                setTimeout(() =>
                  editor.chain().focus().setTextAlign('right').run(),
                )
              }}
            >
              <TextAlignRightIcon className="w-5 h-5" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.TASK_LIST) && (
        <Button
          isActive={editor.isActive('taskList')}
          onClick={() => {
            setOpenDropdown(null)
            editor.chain().focus().toggleTaskList().run()
          }}
        >
          <CheckboxIcon className="w-5 h-5" />
        </Button>
      )}
      {(extensionsList.includes(ExtensionsEnum.COLOR) ||
        extensionsList.includes(ExtensionsEnum.HIGHLIGHT)) && (
        <DropdownMenu
          open={openDropdown === 'colors'}
          onOpenChange={(open) => setOpenDropdown(open ? 'colors' : null)}
        >
          <Trigger>
            <div className="w-5 h-5 flex items-center justify-center relative">
              <span className="text-sm font-semibold">A</span>
              <div
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{
                  backgroundColor:
                    editor.getAttributes('textStyle')?.color || '#373530',
                }}
              />
            </div>
          </Trigger>
          <DropdownMenuContent
            className="min-w-[2rem] grid grid-cols-5 gap-2 p-2"
            onMouseDown={(e) => e.preventDefault()}
          >
            {extensionsList.includes(ExtensionsEnum.COLOR) && (
              <div className="col-span-5 text-xs font-medium py-1">
                Text color
              </div>
            )}
            <ColorPicker
              whiteCheck={true}
              colors={TEXT_COLORS}
              onColorSelect={(color) => {
                setTimeout(() =>
                  editor.chain().focus().toggleTextStyle({ color }).run(),
                )
              }}
              isSelected={(color) =>
                editor.getAttributes('textStyle')?.color === color
              }
            />
            {extensionsList.includes(ExtensionsEnum.HIGHLIGHT) && (
              <div className="col-span-5 text-xs font-medium py-1 mt-2">
                Background color
              </div>
            )}
            <ColorPicker
              colors={HIGHLIGHT_COLORS}
              onColorSelect={(color) => {
                setTimeout(() =>
                  editor.chain().focus().toggleHighlight({ color }).run(),
                )
              }}
              isSelected={(color) =>
                editor.getAttributes('highlight')?.color === color
              }
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.ICONS) && (
        <DropdownMenu
          open={openDropdown === 'icons'}
          onOpenChange={(open) => setOpenDropdown(open ? 'icons' : null)}
        >
          <Trigger>
            <WrenchIcon className="w-5 h-5" />
          </Trigger>
          <IconsMenu editor={editor} />
        </DropdownMenu>
      )}
      {extensionsList.includes(ExtensionsEnum.BACKGROUND) && (
        <DropdownMenu
          open={openDropdown === 'background'}
          onOpenChange={(open) => setOpenDropdown(open ? 'background' : null)}
        >
          <Trigger>
            <OpacityIcon className="w-5 h-5" />
          </Trigger>
          <DropdownMenuContent className="min-w-[2rem] grid grid-cols-5 gap-2 p-2">
            <div className="col-span-5 text-xs font-medium py-1">
              Label background
            </div>
            <ColorPicker
              colors={HIGHLIGHT_COLORS}
              onColorSelect={(color) => {
                setTimeout(() => updateBackgroundColorHandler?.(color))
              }}
              isSelected={(color) => backgroundColor === color}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
